/// NATO / ICAO / ITU-R / IMO phonetic alphabet.
///
/// The single international standard used by every aviation, maritime,
/// and military service since 1956. Note the spelling quirks IMO + ICAO
/// preserve:
///   - "Alfa" (not Alpha) — avoids the silent-PH problem for non-anglophone
///     speakers.
///   - "Juliett" (with double-T) — same reason; signals a hard final T.
/// Digit pronunciations match ICAO Annex 10 — "Tree", "Fower", "Fife",
/// and "Niner" guard against the most-confused number pairs over noisy
/// VHF (3/8, 5/9).

export interface PhoneticEntry {
  /// The character itself ("A", "0", etc.).
  char: string;
  /// The full code word ("Alfa", "Wun").
  word: string;
  /// Stress-mark hint ("AL-fah") for the explainer panel. The
  /// stressed syllable is rendered in caps in the source so the
  /// player learns how it's actually pronounced on the radio.
  pronunciation: string;
}

export const NATO_LETTERS: Readonly<Record<string, PhoneticEntry>> =
  Object.freeze({
    A: { char: "A", word: "Alfa", pronunciation: "AL-fah" },
    B: { char: "B", word: "Bravo", pronunciation: "BRAH-voh" },
    C: { char: "C", word: "Charlie", pronunciation: "CHAR-lee" },
    D: { char: "D", word: "Delta", pronunciation: "DELL-tah" },
    E: { char: "E", word: "Echo", pronunciation: "ECK-oh" },
    F: { char: "F", word: "Foxtrot", pronunciation: "FOKS-trot" },
    G: { char: "G", word: "Golf", pronunciation: "GOLF" },
    H: { char: "H", word: "Hotel", pronunciation: "hoh-TELL" },
    I: { char: "I", word: "India", pronunciation: "IN-dee-ah" },
    J: { char: "J", word: "Juliett", pronunciation: "JEW-lee-ett" },
    K: { char: "K", word: "Kilo", pronunciation: "KEY-loh" },
    L: { char: "L", word: "Lima", pronunciation: "LEE-mah" },
    M: { char: "M", word: "Mike", pronunciation: "MIKE" },
    N: { char: "N", word: "November", pronunciation: "no-VEM-ber" },
    O: { char: "O", word: "Oscar", pronunciation: "OSS-cah" },
    P: { char: "P", word: "Papa", pronunciation: "pah-PAH" },
    Q: { char: "Q", word: "Quebec", pronunciation: "keh-BECK" },
    R: { char: "R", word: "Romeo", pronunciation: "ROW-me-oh" },
    S: { char: "S", word: "Sierra", pronunciation: "see-AIR-rah" },
    T: { char: "T", word: "Tango", pronunciation: "TANG-go" },
    U: { char: "U", word: "Uniform", pronunciation: "YOU-nee-form" },
    V: { char: "V", word: "Victor", pronunciation: "VIK-tah" },
    W: { char: "W", word: "Whiskey", pronunciation: "WISS-key" },
    X: { char: "X", word: "X-ray", pronunciation: "ECKS-ray" },
    Y: { char: "Y", word: "Yankee", pronunciation: "YANG-key" },
    Z: { char: "Z", word: "Zulu", pronunciation: "ZOO-loo" },
  });

export const NATO_DIGITS: Readonly<Record<string, PhoneticEntry>> =
  Object.freeze({
    "0": { char: "0", word: "Zero", pronunciation: "ZEE-roh" },
    "1": { char: "1", word: "Wun", pronunciation: "WUN" },
    "2": { char: "2", word: "Two", pronunciation: "TOO" },
    "3": { char: "3", word: "Tree", pronunciation: "TREE" },
    "4": { char: "4", word: "Fower", pronunciation: "FOW-er" },
    "5": { char: "5", word: "Fife", pronunciation: "FIFE" },
    "6": { char: "6", word: "Six", pronunciation: "SIX" },
    "7": { char: "7", word: "Seven", pronunciation: "SEV-en" },
    "8": { char: "8", word: "Eight", pronunciation: "AIT" },
    "9": { char: "9", word: "Niner", pronunciation: "NIN-er" },
  });

/// Combined map for spelling out arbitrary strings — pass any A–Z, 0–9
/// and get back the code-word; characters not in the table (spaces,
/// punctuation) come back as the original character so a phrase like
/// "MV BOAT 7" reads naturally as Mike Victor Boat Seven with spaces.
export function spellOut(input: string): string[] {
  return Array.from(input.toUpperCase()).map((c) => {
    const entry = NATO_LETTERS[c] ?? NATO_DIGITS[c];
    return entry?.word ?? c;
  });
}

export const NATO_KEYS: readonly string[] = [
  ...Object.keys(NATO_LETTERS),
  ...Object.keys(NATO_DIGITS),
];

/// Plausible-but-wrong words for each NATO key. Used as distractors
/// in the char-to-word flashcards so the player has to actually
/// recall "Foxtrot" rather than just spot the only F-word in a list
/// of unrelated other NATO entries. The decoy words start with the
/// same letter (or sound) as the correct answer.
export const NATO_CONFUSERS: Readonly<Record<string, readonly string[]>> =
  Object.freeze({
    A: ["Apple", "Alpha", "Andrew", "Anchor", "April", "Atlas"],
    B: ["Banana", "Beta", "Bottle", "Bridge", "Boston", "Brian"],
    C: ["Carbon", "Cardinal", "Chip", "Cobra", "Calico", "Carl"],
    D: ["Doctor", "Diamond", "Daniel", "Dover", "Dingo", "David"],
    E: ["Eagle", "Ember", "Easy", "Earth", "Edward", "Egypt"],
    F: ["Fox", "Fish", "Flag", "Fire", "Foster", "Falcon"],
    G: ["George", "Galaxy", "Grand", "Gulf", "Geneva", "Goose"],
    H: ["Henry", "Honey", "Hat", "Helmet", "Halifax", "Hawk"],
    I: ["Igloo", "Ice", "Iceland", "Indigo", "Italy", "Ivan"],
    J: ["Juliet", "Jet", "Jacket", "Jupiter", "Java", "James"],
    K: ["King", "Kite", "Kilowatt", "Kettle", "Kenya", "Karl"],
    L: ["London", "Lion", "Lamp", "Lemon", "Lambda", "Larry"],
    M: ["Michael", "Money", "Mountain", "Mars", "Mexico", "Mary"],
    N: ["Nancy", "Nelson", "Norway", "North", "Niagara", "Neptune"],
    O: ["Olive", "October", "Orange", "Oxford", "Omega", "Owen"],
    P: ["Peter", "Paris", "Panda", "Pepper", "Penguin", "Phoenix"],
    Q: ["Queen", "Quiz", "Quill", "Quattro", "Quartz", "Quake"],
    R: ["Robert", "Radio", "Roger", "Rome", "Russia", "Raven"],
    S: ["Sigma", "Silver", "Sand", "Sail", "Sahara", "Storm"],
    T: ["Tom", "Tiger", "Travel", "Texas", "Tobago", "Titan"],
    U: ["Umbrella", "Union", "Uncle", "Utopia", "Uganda", "Ulysses"],
    V: ["Vincent", "Vegas", "Vault", "Verdict", "Vienna", "Volt"],
    W: ["William", "Walker", "Wind", "Wireless", "Wales", "Wolf"],
    X: ["Xenon", "Xylophone", "X-factor", "Xerox", "Xanadu", "Xander"],
    Y: ["Yellow", "York", "Yacht", "Yukon", "Yale", "Yale"],
    Z: ["Zebra", "Zero", "Zenith", "Zion", "Zurich", "Zephyr"],
    "0": ["Zilch", "Nil", "Nothing", "Oh", "Naught"],
    "1": ["One", "Ace", "Solo", "Single", "Uno"],
    "2": ["Double", "Pair", "Duet", "Twin", "Twosome"],
    "3": ["Three", "Triple", "Triad", "Trio", "Trey"],
    "4": ["Four", "Quad", "Square", "Tetra", "Forty"],
    "5": ["Five", "Pent", "Quint", "Fifty", "Penta"],
    "6": ["Sixer", "Hex", "Senary", "Sixteen", "Sixty"],
    "7": ["Seventy", "Lucky", "Seventh", "Sevens", "Septa"],
    "8": ["Octo", "Eighteen", "Octave", "Octet", "Eighty"],
    "9": ["Nine", "Ninety", "Nonagon", "Nona", "Nineteen"],
  });
